import { useState } from "react";
import { Text, Button, StatusChip } from "@fragments";
import {
    IconPlus,
    IconRefresh,
    IconSquareRoundedMinus,
    IconAlertTriangle,
    IconChecks,
} from "@tabler/icons-react";
import * as Mui from "@mui/material";

const DOCS_LABELS = {
    ALLOW_BANK_ACCOUNT_DEPOSIT_STATEMENT: "Comprovante de conta bancária",
    CUSTOM: "Documento personalizado",
    EMANCIPATION_OF_MINORS: "Emancipação de menores",
    ENTREPRENEUR_REQUIREMENT: "Requerimento de empresário",
    IDENTIFICATION_SELFIE: "Selfie com documento de identidade",
    IDENTIFICATION: "RG ou CNH",
    INVOICE: "Nota Fiscal",
    MEI_CERTIFICATE: "Certificado MEI",
    MINUTES_OF_CONSTITUTION: "Ata de constituição",
    MINUTES_OF_ELECTION: "Ata de eleição",
    POWER_OF_ATTORNEY: "Procuração",
    SOCIAL_CONTRACT: "Contrato Social"
};

const STATUS_CONFIG = {
    pending: { text: "Aguardando envio", variant: "default", Icon: IconSquareRoundedMinus },
    rejected: { text: "Reprovado", variant: "error", Icon: IconAlertTriangle },
    completed: { text: "Concluído", variant: "success", Icon: IconChecks },
    approved: { text: "Aprovado", variant: "success", Icon: IconChecks },
    awaiting_approval: { text: "Em análise", variant: "info", Icon: IconRefresh },
};

export default function DocumentRow({ document, onUpload, onRefresh }) {
    const [docLoading, setDocLoading] = useState(false);
    const [showRefresh, setShowRefresh] = useState(false);

    const config = STATUS_CONFIG[document.status] || STATUS_CONFIG.pending;
    const { Icon } = config;
    const canUpload = ['pending', 'rejected'].includes(document.status);

    async function handleUpload() {
        if (document.url) {
            window.open(document.url, '_blank');

            let refreshed = false;
            const cleanup = () => {
                window.document.removeEventListener('visibilitychange', onVisibilityChange);
                window.removeEventListener('focus', onFocus);
            };
            const triggerRefresh = () => {
                if (refreshed) return;
                refreshed = true;
                cleanup();
                onRefresh?.();
            };
            const onVisibilityChange = () => { if (!window.document.hidden) triggerRefresh(); };
            const onFocus = () => triggerRefresh();

            window.document.addEventListener('visibilitychange', onVisibilityChange);
            window.addEventListener('focus', onFocus);
            return;
        }
        setShowRefresh(true);
        await onUpload(document, setDocLoading);
        setShowRefresh(false);
    }

    async function handleRefresh() {
        setDocLoading(true);
        await onRefresh?.();
        setDocLoading(false);
        setShowRefresh(false);
    }

    return (
        <div className="document-row">
            <div className="document-row-left">
                <StatusChip
                    variant={config.variant}
                    text={config.text}
                    icon={
                        <Icon
                            className={document.status === 'awaiting_approval' ? 'analysis-spin' : ''}
                            size={16}
                        />
                    }
                />
            </div>

            <Text.h5 className="document-row-label" contrast={7}>
                {DOCS_LABELS[document.type?.toUpperCase()] || document.type}
            </Text.h5>

            <div className="document-row-actions">
                {docLoading && (
                    <Mui.CircularProgress size={18} thickness={5} sx={{ color: 'var(--primary-5)' }} />
                )}

                {canUpload && !docLoading && (
                    <Button
                        size="small"
                        color="primary"
                        endIcon={!document.url ? <IconPlus /> : undefined}
                        onClick={handleUpload}
                    >
                        {document.url ? 'Acessar' : 'Enviar'}
                    </Button>
                )}

                {showRefresh && !docLoading && (
                    <Button
                        size="small"
                        color="default"
                        endIcon={<IconRefresh />}
                        onClick={handleRefresh}
                    >
                        Atualizar
                    </Button>
                )}
            </div>
        </div>
    );
}
